/**
 * Adding a task, in a modal rather than on its own page.
 *
 * A task is a few short fields and is almost always written down while something
 * else is being read. Navigating away to record one loses the place you were
 * keeping, so this opens over the list and closes back onto it.
 *
 * **Assignment is a second call, not a field on the create.** Handing work to
 * somebody notifies them, so the service treats it as its own act with its own
 * audit record — a task must not be able to appear in a colleague's list with no
 * trace of who put it there. The form still asks, because the common case is a
 * note to yourself and a task nobody owns is a task nobody does.
 *
 * Leaving it unassigned is permitted and sometimes right: work is often
 * identified before it is clear who will take it, and refusing to record it
 * until then is how it gets forgotten instead.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type FirmMember,
  type TaskListItem,
} from '@legalcrm/api';
import {
  Form,
  FormActions,
  FormDate,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  useZodForm,
} from '@legalcrm/ui';
import { z } from 'zod';

import { useAuth } from '@/auth/AuthProvider';

const schema = z.object({
  title: z.string().min(1, 'Say what needs doing'),
  description: z.string(),
  priority: z.string(),
  due_at: z.string(),
  assignee: z.string(),
});

type Values = z.infer<typeof schema>;

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export interface NewTaskModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-attach the task to a matter, when opened from one. */
  subjectType?: string;
  subjectId?: string;
}

export function NewTaskModal({ open, onClose, subjectType, subjectId }: NewTaskModalProps) {
  const api = useApi();
  const { me } = useAuth();

  const members = useApiQuery<{ results: FirmMember[] } | FirmMember[]>(
    qk.list('users'),
    STAFF.users,
  );
  const memberRows = Array.isArray(members.data)
    ? members.data
    : (members.data?.results ?? []);

  // Defaulting to the signed-in person is what makes the common case work: most
  // roles see only tasks assigned to them, so a task they raised and left unowned
  // would vanish from their own list the moment they saved it.
  const myUserId = memberRows.find((row) => row.email === me?.user.email)?.user ?? '';

  const form = useZodForm(schema, {
    defaultValues: {
      title: '',
      description: '',
      priority: 'normal',
      due_at: '',
      assignee: myUserId,
    },
    values: {
      title: '',
      description: '',
      priority: 'normal',
      due_at: '',
      assignee: myUserId,
    },
  });

  const create = useApiMutation<TaskListItem, Values>({
    mutationFn: async (values) => {
      const task = await api.post<TaskListItem>(`${STAFF.tasks}/`, {
        title: values.title,
        description: values.description || undefined,
        priority: values.priority,
        // A date with no time is midnight, which would read as overdue all day.
        // Sent as end of day so "due Friday" means what a person means by it.
        due_at: values.due_at ? `${values.due_at}T17:00:00Z` : undefined,
        subject_type: subjectType,
        subject_id: subjectId,
      });

      if (values.assignee) {
        await api.post(`${STAFF.task(task.id)}/assign/`, { assignee: values.assignee });
      }
      return task;
    },
    invalidates: [qk.all('tasks'), qk.dashboard()],
  });

  return (
    <Modal open={open} onClose={onClose} title="New task">
      <Form
        form={form}
        ariaLabel="New task"
        error={create.error}
        onSubmit={async (values) => {
          await create.mutateAsync(values);
          form.reset();
          onClose();
        }}
      >
        <FormInput form={form} name="title" label="What needs doing" autoFocus />

        <FormSelect
          form={form}
          name="assignee"
          label="Who is doing it"
          hint="Leave empty if it is not yet clear who will take it."
          options={[
            { value: '', label: 'Nobody yet' },
            ...memberRows
              .filter((row) => row.status === 'active')
              .map((row) => ({
                value: row.user,
                label: row.user === myUserId ? `${row.full_name} (me)` : row.full_name,
              })),
          ]}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormSelect form={form} name="priority" label="Priority" options={PRIORITIES} />
          <FormDate form={form} name="due_at" label="Due" />
        </div>

        <FormTextarea form={form} name="description" label="Notes" rows={3} />

        <FormActions
          submitLabel="Add task"
          busy={create.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
